import { useEffect, useState, useRef } from "react";
import { Responsive, Layout } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import {
  Folder,
  Clock,
  CheckCircle,
  AlertTriangle,
  LayoutDashboard,
  Save,
  RotateCcw,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { analyticsApi, DashboardAnalytics } from "@/services/analyticsService";
import { PipelineValueWidget } from "./widgets/PipelineValueWidget";
import { ServiceDistributionWidget } from "./widgets/ServiceDistributionWidget";
import { CycleTimeWidget } from "./widgets/CycleTimeWidget";
import { StatusCardWidget } from "./widgets/StatusCardWidget";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";

import { ProjectsTrendWidget } from "./widgets/ProjectsTrendWidget";
import { AvgProjectValueWidget } from "./widgets/AvgProjectValueWidget";
import { RevenueByServiceWidget } from "./widgets/RevenueByServiceWidget";
import { SuccessRateWidget } from "./widgets/SuccessRateWidget";
import { StorageTypeWidget } from "./widgets/StorageTypeWidget";

// Custom WidthProvider implementation since the library export seems broken/missing in this version
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const withWidth = (WrappedComponent: any) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (props: any) => {
    const ref = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(1200);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
      setMounted(true);
      const element = ref.current;
      if (!element) return;

      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          // Use contentRect.width for precise inner width
          setWidth(entry.contentRect.width);
        }
      });

      resizeObserver.observe(element);
      // Initial measure
      setWidth(element.offsetWidth);

      return () => {
        resizeObserver.disconnect();
      };
    }, []);

    return (
      <div
        ref={ref}
        className={props.className}
        style={{ ...props.style, width: "100%" }}
      >
        {mounted && <WrappedComponent {...props} width={width} />}
      </div>
    );
  };
};

const ResponsiveGridLayout = withWidth(Responsive);

interface MyLayout {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  maxW?: number;
  minH?: number;
  maxH?: number;
  static?: boolean;
  isDraggable?: boolean;
  isResizable?: boolean;
}

type MyLayouts = { [key: string]: MyLayout[] };

const DEFAULT_LAYOUT: MyLayouts = {
  lg: [
    {
      i: "analytics-pipeline",
      x: 0,
      y: 0,
      w: 1,
      h: 2,
      minW: 1,
      minH: 2,
    },
    {
      i: "analytics-service",
      x: 6,
      y: 3,
      w: 2,
      h: 3,
      minW: 2,
      minH: 2,
    },
    {
      i: "analytics-cycletime",
      x: 4,
      y: 0,
      w: 2,
      h: 3,
      minW: 2,
      minH: 3,
    },
    {
      i: "analytics-trend",
      x: 0,
      y: 2,
      w: 4,
      h: 4,
      minW: 4,
      minH: 3,
    },
    {
      i: "analytics-avg-value",
      x: 1,
      y: 0,
      w: 1,
      h: 2,
      minW: 1,
      minH: 2,
    },
    {
      i: "analytics-revenue",
      x: 6,
      y: 0,
      w: 2,
      h: 3,
      minW: 2,
      minH: 3,
    },
    {
      i: "analytics-success",
      x: 2,
      y: 0,
      w: 2,
      h: 2,
      minW: 2,
      minH: 2,
    },
    {
      i: "analytics-storage",
      x: 4,
      y: 3,
      w: 2,
      h: 3,
      minW: 2,
      minH: 2,
    },
  ],
  md: [
    // Medium screens (Tablets): Using a 2-column stacked approach
    { i: "analytics-pipeline", x: 0, y: 0, w: 2, h: 2 },
    { i: "analytics-avg-value", x: 2, y: 0, w: 2, h: 2 },
    { i: "analytics-success", x: 0, y: 2, w: 4, h: 2 }, // Success gets wider on tablet

    { i: "analytics-trend", x: 0, y: 4, w: 4, h: 4 },
    { i: "analytics-revenue", x: 0, y: 8, w: 4, h: 4 },

    { i: "analytics-cycletime", x: 0, y: 12, w: 2, h: 4 },
    { i: "analytics-service", x: 2, y: 12, w: 2, h: 4 },
    { i: "analytics-storage", x: 0, y: 16, w: 4, h: 4 },
  ],
  sm: [
    // Single column stack for mobile
    { i: "analytics-pipeline", x: 0, y: 0, w: 2, h: 2 },
    { i: "analytics-avg-value", x: 0, y: 2, w: 2, h: 2 },
    { i: "analytics-success", x: 0, y: 4, w: 2, h: 2 },
    { i: "analytics-trend", x: 0, y: 6, w: 2, h: 4 },
    { i: "analytics-revenue", x: 0, y: 10, w: 2, h: 4 },
    { i: "analytics-cycletime", x: 0, y: 14, w: 2, h: 3 },
    { i: "analytics-service", x: 0, y: 17, w: 2, h: 4 },
    { i: "analytics-storage", x: 0, y: 21, w: 2, h: 4 },
  ],
};

export const AnalyticsDashboard = () => {
  const [analytics, setAnalytics] = useState<DashboardAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isAnalyticsVisible, setIsAnalyticsVisible] = useState(true);
  const [layouts, setLayouts] = useState<MyLayouts>(DEFAULT_LAYOUT);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const data = await analyticsApi.getDashboardAnalytics();
        setAnalytics(data);
      } catch (error) {
        console.error("Failed to fetch analytics", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalytics();

    // Load saved layout
    const savedLayout = localStorage.getItem("dashboard-layout");
    if (savedLayout) {
      try {
        setLayouts(JSON.parse(savedLayout));
      } catch (e) {
        console.error("Failed to parse saved layout", e);
      }
    }
  }, []);

  const handleLayoutChange = (layout: MyLayout[], allLayouts: MyLayouts) => {
    setLayouts(allLayouts);
    // Auto-save layout changes
    localStorage.setItem("dashboard-layout", JSON.stringify(allLayouts));
  };

  const exitEditMode = () => {
    setIsEditMode(false);
  };

  const resetLayout = () => {
    setLayouts(DEFAULT_LAYOUT);
    localStorage.removeItem("dashboard-layout");
    toast({
      title: "Layout Reset",
      description: "Dashboard returned to default layout.",
    });
  };

  const getStatusValue = (status: string) => {
    if (!analytics) return 0;
    // Map status string to the key in statusDistribution
    // Note: Backend returns exact enum values (e.g., "draft", "in progress")
    return analytics.statusDistribution[status] || 0;
  };

  const statusWidgets = [
    {
      id: "stat-draft",
      label: "Draft",
      status: "draft",
      icon: Folder,
      color: "text-muted-foreground",
    },
    {
      id: "stat-pending",
      label: "Pending",
      status: "pending",
      icon: Folder,
      color: "text-status-pending",
    },
    {
      id: "stat-submitted",
      label: "Submitted",
      status: "submitted",
      icon: Folder,
      color: "text-status-submitted",
    },
    {
      id: "stat-inprogress",
      label: "In Progress",
      status: "in progress",
      icon: Folder,
      color: "text-status-inProgress",
    },
    {
      id: "stat-completed",
      label: "Completed",
      status: "completed",
      icon: CheckCircle,
      color: "text-status-approved",
    },
    {
      id: "stat-onhold",
      label: "On Hold",
      status: "on hold",
      icon: Clock,
      color: "text-status-pending",
    },
    {
      id: "stat-rejected",
      label: "Rejected",
      status: "rejected",
      icon: AlertTriangle,
      color: "text-status-rejected",
    },
    {
      id: "stat-total",
      label: "Total",
      status: "total",
      icon: Folder,
      color: "text-muted-foreground",
    },
  ];

  return (
    <div className="space-y-4">
      {/* <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold">Analytics Overview</h2>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setIsAnalyticsVisible(!isAnalyticsVisible)}
          >
            {isAnalyticsVisible ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        </div>
        <div className="flex gap-2">
          {isEditMode ? (
            <>
              <Button variant="outline" size="sm" onClick={resetLayout}>
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset
              </Button>
              <Button size="sm" onClick={exitEditMode}>
                <CheckCircle className="h-4 w-4 mr-2" />
                Done
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditMode(true)}
            >
              <LayoutDashboard className="h-4 w-4 mr-2" />
              Customize Dashboard
            </Button>
          )}
        </div>
      </div> */}

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        {statusWidgets.map((widget) => (
          <StatusCardWidget
            key={widget.id}
            label={widget.label}
            value={
              widget.status === "total"
                ? analytics?.totalProjects || 0
                : getStatusValue(widget.status)
            }
            icon={widget.icon}
            color={widget.color}
            isLoading={isLoading}
            onClick={() =>
              navigate(
                widget.status === "total"
                  ? "/projects"
                  : `/projects?status=${widget.status.replace(" ", "_")}`
              )
            }
          />
        ))}
      </div>

      {/* {isAnalyticsVisible && (
        <ResponsiveGridLayout
          className="layout"
          layouts={layouts}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
          cols={{ lg: 8, md: 4, sm: 2, xs: 1, xxs: 1 }}
          rowHeight={100}
          isDraggable={isEditMode}
          isResizable={isEditMode}
          onLayoutChange={handleLayoutChange}
          draggableHandle=".drag-handle"
        >
          <div
            key="analytics-pipeline"
            className={isEditMode ? "cursor-move drag-handle" : ""}
          >
            <PipelineValueWidget
              value={analytics?.totalPipelineValue || 0}
              totalProjects={analytics?.totalProjects || 0}
            />
          </div>

          <div
            key="analytics-service"
            className={isEditMode ? "cursor-move drag-handle" : ""}
          >
            <ServiceDistributionWidget
              data={analytics?.serviceDistribution || {}}
            />
          </div>

          <div
            key="analytics-cycletime"
            className={isEditMode ? "cursor-move drag-handle" : ""}
          >
            <CycleTimeWidget
              draftToSubmitted={analytics?.avgCycleTime.draftToSubmitted || 0}
              submittedToCompleted={
                analytics?.avgCycleTime.submittedToCompleted || 0
              }
            />
          </div>

          <div
            key="analytics-trend"
            className={isEditMode ? "cursor-move drag-handle" : ""}
          >
            <ProjectsTrendWidget data={analytics?.projectsTrend || []} />
          </div>

          <div
            key="analytics-avg-value"
            className={isEditMode ? "cursor-move drag-handle" : ""}
          >
            <AvgProjectValueWidget value={analytics?.avgProjectValue || 0} />
          </div>

          <div
            key="analytics-revenue"
            className={isEditMode ? "cursor-move drag-handle" : ""}
          >
            <RevenueByServiceWidget data={analytics?.revenueByService || {}} />
          </div>

          <div
            key="analytics-success"
            className={isEditMode ? "cursor-move drag-handle" : ""}
          >
            <SuccessRateWidget rate={analytics?.submissionSuccessRate || 0} />
          </div>

          <div
            key="analytics-storage"
            className={isEditMode ? "cursor-move drag-handle" : ""}
          >
            <StorageTypeWidget
              data={analytics?.storageTypeDistribution || {}}
            />
          </div>
        </ResponsiveGridLayout>
      )} */}
    </div>
  );
};
